import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Fade, Icon, Input, Tooltip, formatNumber } from '@column/column-ui-kit';

import { CustomPageContext, PageTabs, useBreadcrumbTitle } from '~/app/Layout';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput } from '~/components';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { Loan, LoanRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Line, Text } from '~/styles';

interface Params {
  id: string;
}

export interface LoanProps {
  loan: Loan;
}

interface LoanForm {
  description: string;
  isRevolving: boolean;
  maturityDate?: string;
  maxPrincipalBalance?: number;
  currency?: string;
  status?: string;
  primarySignerEntityId?: string;
  entityName?: string;
  createdAt?: string;
  chargedOffAt?: string;
  paidOffAt?: string;
  delinquentAt?: string;
  disputedAt?: string;
}

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

const Balance = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-column: 1 / 3;
  grid-gap: 16px;

  @media (min-width: 400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 88px;
  }

  &[data-disabled='true'] {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: inherit;
      cursor: not-allowed;
      z-index: 11;
      transition: background-color 0.2s;
    }

    &:hover {
      &:before {
        background-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
      }
    }
  }
`;

const StyledLine = styled(Line)`
  background-color: ${({ theme }) => theme.secondary.blendToBackground(900)};
`;

const IconQuestionmark = styled(Icon.CircleQuestionmark)`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  --icon-size: 14px;
  margin-left: 4px;
`;

const DescriptionWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const EditButton = styled(Button)`
  opacity: 0;
  transition: opacity 0.2s;
  padding: 4px;
  min-width: auto;
  height: auto;

  ${DescriptionWrapper}:hover & {
    opacity: 1;
  }
`;

const EditIcon = styled(Icon.Edit)`
  --icon-size: 12px;
`;

const DescriptionInput = styled(Input)`
  width: 100%;
  padding-left: 12px;
  padding-right: 12px;
  max-height: 32px;
`;

const LoanHeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

const loanQueryKey = (id: string) => ['loan', id] as const;

const EditableDescription: FC<{ value: string; onChange: (value: string) => void }> = ({
  value: propValue,
  onChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(propValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setValue(propValue);
    }
  }, [propValue, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    onChange(value);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setValue(propValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <DescriptionInput
        ref={inputRef}
        value={value}
        onChange={(v) => setValue(v)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        size="small"
      />
    );
  }

  return (
    <DescriptionWrapper onClick={() => setIsEditing(true)}>
      <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
        {value || 'Loan'}
      </Text>
      <EditButton
        variant="subtle"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      >
        <EditIcon />
      </EditButton>
    </DescriptionWrapper>
  );
};

export const PageLoan: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: loan,
    isLoading,
    error: loanError,
  } = useQuery({
    queryKey: loanQueryKey(id!),
    queryFn: () => LoanRepository.get(id!),
    enabled: !!id,
  });

  const formMethods = useForm<LoanForm>({
    defaultValues: {
      description: '',
      isRevolving: false,
      maturityDate: undefined,
      maxPrincipalBalance: undefined,
      currency: 'USD',
      status: 'current',
      primarySignerEntityId: undefined,
      entityName: '',
      createdAt: undefined,
      chargedOffAt: undefined,
      paidOffAt: undefined,
      delinquentAt: undefined,
      disputedAt: undefined,
    },
  });

  const { watch, reset, setValue, formState } = formMethods;
  const description = watch('description');
  const hasChanges = formState.isDirty;

  useBreadcrumbTitle(description || loan?.description);

  const isPaymentsTab = location.pathname.endsWith('/payments');
  const isDisbursementsTab = location.pathname.endsWith('/disbursements');
  const isCreatePayment = location.pathname.endsWith('/payment');
  const isCreateDisbursement = location.pathname.endsWith('/disbursement');
  const isCreateRoute = isCreatePayment || isCreateDisbursement;

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        (newState, oldState) => {
          if (!currentUser) {
            return;
          }

          if (newState.isSandbox !== oldState.isSandbox || newState.defaultPlatformId !== oldState.defaultPlatformId) {
            if (id) {
              queryClient.invalidateQueries({ queryKey: loanQueryKey(id) });
            }
          }
        }
      ),
    [id, currentUser, queryClient]
  );

  useEffect(() => {
    if (loanError) {
      navigate(ROUTE.LOANS);
      addDangerNotification({
        content: (loanError as any).message || 'Failed to load loan',
      });
    }
  }, [loanError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (loan ? { loan } : undefined), [loan]);

  useEffect(() => {
    if (!loan) return;
    reset({
      description: loan.description ?? '',
      isRevolving: loan.isRevolving ?? false,
      maturityDate: loan.maturityDate ?? undefined,
      maxPrincipalBalance: loan.maxPrincipalBalance ? parseInt(loan.maxPrincipalBalance, 10) : undefined,
      currency: loan.currency ?? 'USD',
      status: loan.status ?? 'current',
      primarySignerEntityId: loan.primarySignerEntityId ?? undefined,
      createdAt: loan.createdAt ?? undefined,
      chargedOffAt: loan.chargedOffAt ?? undefined,
      paidOffAt: loan.paidOffAt ?? undefined,
      delinquentAt: loan.delinquentAt ?? undefined,
      disputedAt: loan.disputedAt ?? undefined,
      entityName: '',
    });
  }, [loan, reset]);

  const handleCancel = useCallback(() => {
    if (!loan) return;
    reset({
      description: loan.description ?? '',
      isRevolving: loan.isRevolving ?? false,
      maturityDate: loan.maturityDate ?? undefined,
      maxPrincipalBalance: loan.maxPrincipalBalance ? parseInt(loan.maxPrincipalBalance, 10) : undefined,
      currency: loan.currency ?? 'USD',
      status: loan.status ?? 'current',
      primarySignerEntityId: loan.primarySignerEntityId ?? undefined,
      createdAt: loan.createdAt ?? undefined,
      chargedOffAt: loan.chargedOffAt ?? undefined,
      paidOffAt: loan.paidOffAt ?? undefined,
      delinquentAt: loan.delinquentAt ?? undefined,
      disputedAt: loan.disputedAt ?? undefined,
      entityName: '',
    });
  }, [loan, reset]);

  const formId = 'loan-information-form';

  const handleSave = useCallback(() => {
    const form = document.getElementById(formId) as HTMLFormElement;
    form?.requestSubmit();
  }, [formId]);

  return (
    <>
      <StyledFade show={isLoading || !loan}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!isLoading && !!loan}>
        <FormProvider {...formMethods}>
          {!isCreateRoute && (
            <>
              <PageActions>
                {hasChanges && (
                  <>
                    <Button variant="secondary" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave}>
                      Save Changes
                    </Button>
                  </>
                )}
                {isPaymentsTab && loan?.id && (
                  <Button
                    onClick={() => navigate(`${ROUTE.LOANS}/edit/${loan.id}/payment`)}
                    icon={<Icon.Plus />}
                    size="small"
                  >
                    Create Payment
                  </Button>
                )}
                {isDisbursementsTab && loan?.id && (
                  <Button
                    onClick={() => navigate(`${ROUTE.LOANS}/edit/${loan.id}/disbursement`)}
                    icon={<Icon.Plus />}
                    size="small"
                  >
                    Create Disbursement
                  </Button>
                )}
              </PageActions>
              <PageTabs entries={entries} />
            </>
          )}
          {!isCreateRoute && id && loan && (
            <>
              <Inner pt={12} px="var(--page-margin)" pb={16}>
                <Flex flexDirection="column" gap={12}>
                  <LoanHeaderRow>
                    <EditableDescription
                      value={description || loan.description || 'Untitled Loan'}
                      onChange={(value) => setValue('description', value)}
                    />

                    <InlineCopyInputWrapper>
                      <InlineCopyInput label="Loan ID" value={loan.id} minWidth="300px" isMonospaced />
                    </InlineCopyInputWrapper>
                  </LoanHeaderRow>

                  <Balance>
                    <Flex flexDirection="column">
                      <Text size={20} lineHeight="28px" weight="semibold" color="foreground" letterSpacing="tight">
                        {formatNumber(loan.balances?.principalOutstanding)}
                      </Text>
                      <Text size={12} color="light" weight="normal">
                        Outstanding
                      </Text>
                    </Flex>

                    <Flex flexDirection="column">
                      <Text size={20} lineHeight="28px" weight="semibold" color="foreground" letterSpacing="tight">
                        {formatNumber(
                          Number(loan.maxPrincipalBalance ?? 0) - Number(loan.balances?.principalOutstanding ?? 0)
                        )}
                      </Text>

                      <Tooltip
                        content={
                          <>
                            <Text size={12} color="inherit" weight="medium">
                              &nbsp;&nbsp;&nbsp;Max Principal Balance
                            </Text>
                            <Text size={12} color="inherit" weight="medium">
                              − Principal Outstanding
                            </Text>
                            <StyledLine />
                            <Text size={12} color="inherit" weight="medium">
                              = Available Credit
                            </Text>
                          </>
                        }
                      >
                        <Flex alignItems="center">
                          <Text size={12} color="light" weight="normal" whiteSpace="nowrap">
                            Available Credit
                          </Text>

                          <IconQuestionmark />
                        </Flex>
                      </Tooltip>
                    </Flex>

                    <Flex flexDirection="column">
                      <Text size={20} lineHeight="28px" weight="semibold" color="foreground" letterSpacing="tight">
                        {formatNumber(loan.balances?.retainedPrincipalOutstanding)}
                      </Text>
                      <Text size={12} color="light" weight="normal" whiteSpace="nowrap">
                        Retained outstanding
                      </Text>
                    </Flex>

                    <Flex flexDirection="column">
                      <Text size={20} lineHeight="28px" weight="semibold" color="foreground" letterSpacing="tight">
                        {formatNumber(loan.balances?.interestReceivable)}
                      </Text>
                      <Text size={12} color="light" weight="normal" whiteSpace="nowrap">
                        Interest Receivable
                      </Text>
                    </Flex>
                  </Balance>
                </Flex>
              </Inner>
              <Line />
            </>
          )}
          {outletContext && <Outlet context={outletContext} />}
        </FormProvider>
      </Fade>
    </>
  );
};
